// ---------------------------------------------------------------------------
// Social Proof Renderer
// ---------------------------------------------------------------------------

import { registerRenderer } from "../registry.js";
import type { RenderContext } from "../types.js";
import { attr, text, url } from "../escape.js";

interface SocialProofItem {
  value?: string;
  label?: string;
  imageUrl?: string;
  alt?: string;
}

interface SocialProofProps {
  variant: "logos" | "stats" | "badges";
  heading?: string;
  items: SocialProofItem[];
}

function renderStatsCards(items: SocialProofItem[]): string {
  return items
    .map(
      (item) =>
        `    <div class="ease-social-stat ease-social-stat--card">
      <span class="ease-social-stat-value">${text(item.value ?? "")}</span>
      <span class="ease-social-stat-label">${text(item.label ?? "")}</span>
    </div>`,
    )
    .join("\n");
}

function renderStatsInline(items: SocialProofItem[]): string {
  return items
    .map(
      (item) =>
        `<span class="ease-social-stat ease-social-stat--inline"><strong>${text(item.value ?? "")}</strong> ${text(item.label ?? "")}</span>`,
    )
    .join('<span class="ease-social-separator" aria-hidden="true"> | </span>');
}

function renderLogosOrBadges(items: SocialProofItem[]): string {
  return items
    .map(
      (item) =>
        `    <div class="ease-social-logo">
      <img src="${url(item.imageUrl ?? "")}" alt="${attr(item.alt ?? "")}" loading="lazy">
    </div>`,
    )
    .join("\n");
}

function render(props: Record<string, unknown>, _ctx: RenderContext): string {
  const p = props as unknown as SocialProofProps;

  const variant = p.variant ?? "stats";
  const items = Array.isArray(p.items) ? p.items : [];

  const headingHtml = p.heading
    ? `<h2 class="ease-social-heading">${text(p.heading)}</h2>\n  `
    : "";

  let contentHtml: string;

  if (variant === "stats") {
    // Default to cards style
    contentHtml = `<div class="ease-social-stats" style="display: flex; gap: var(--ease-spacing-lg, 2rem); justify-content: center; flex-wrap: wrap;">
${renderStatsCards(items)}
  </div>`;
  } else {
    contentHtml = `<div class="ease-social-items" style="display: flex; gap: var(--ease-spacing-lg, 2rem); align-items: center; justify-content: center; flex-wrap: wrap;">
${renderLogosOrBadges(items)}
  </div>`;
  }

  return `<section class="ease-section ease-social-proof ease-social-proof--${attr(variant)}" aria-label="${attr(p.heading ?? "Social proof")}">
  ${headingHtml}${contentHtml}
</section>`;
}

// Export inline renderer for use by other components if needed
export { renderStatsInline };

registerRenderer("social-proof", { render });
